// 事務所の名簿と場所（Firestore）。名前・住所などは API を通らず、Security Rules を通して直接読む（仕様書「個人情報の置き場所と守り方」）
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, where, writeBatch } from 'firebase/firestore';
import type { DriverDoc, PlaceDoc, RoutineDoc, StaffDoc, TemplateDoc } from '@torapon/shared';
import { fsdb, fsVersion } from './firebase';

type Dir = { drivers: Map<string, DriverDoc>; staff: Map<string, StaffDoc>; places: Map<string, PlaceDoc>; templates: Map<string, TemplateDoc>; routines: Map<string, RoutineDoc>; ready: boolean };
const empty: Dir = { drivers: new Map(), staff: new Map(), places: new Map(), templates: new Map(), routines: new Map(), ready: false };
const DirCtx = createContext<Dir>(empty);
export const useDirectory = () => useContext(DirCtx);

/** 名前を引く。まだ読めていない・消えたものは「—」 */
export function useNames() {
  const d = useDirectory();
  return {
    driver: (id: string | null | undefined) => (id && d.drivers.get(id)?.name) || '—',
    staff: (id: string | null | undefined) => (id && d.staff.get(id)?.name) || '—',
    place: (id: string | null | undefined) => (id && d.places.get(id)?.name) || '—',
    template: (id: string | null | undefined) => (id && d.templates.get(id)?.name) || '（名前なし）',
    routine: (id: string | null | undefined) => (id && d.routines.get(id)?.name) || '（名前なし）',
  };
}

/** 一覧の上限（Security Rules と同じ） */
const MAX = 500;

export function DirectoryProvider({ officeId, children }: { officeId: string; children: ReactNode }) {
  const [dir, setDir] = useState<Dir>(empty);
  // ログインし直すと Firestore が作り直されるので、そのたびに読み直す
  const version = fsVersion();
  useEffect(() => {
    const db = fsdb();
    const got = new Set<string>();
    const sub = <T,>(name: 'drivers' | 'staff' | 'places' | 'templates' | 'routines') => onSnapshot(
      query(collection(db, name), where('officeId', '==', officeId), limit(MAX)),
      (snap) => {
        got.add(name);
        setDir((d) => ({ ...d, [name]: new Map(snap.docs.map((x) => [x.id, x.data() as T])), ready: got.size === 5 }));
      },
      () => { got.add(name); setDir((d) => ({ ...d, ready: got.size === 5 })); },
    );
    const offs = [sub<DriverDoc>('drivers'), sub<StaffDoc>('staff'), sub<PlaceDoc>('places'), sub<TemplateDoc>('templates'), sub<RoutineDoc>('routines')];
    return () => offs.forEach((f) => f());
  }, [officeId, version]);
  return <DirCtx.Provider value={dir}>{children}</DirCtx.Provider>;
}

/** 初めてのログイン：登録されたアドレスと同じなら、自分の UID を名簿にひもづける（仕様書「本人とのひもづけ」） */
export async function linkIfNeeded(u: { uid: string; email: string }): Promise<boolean> {
  const db = fsdb();
  if ((await getDoc(doc(db, 'members', u.uid))).exists()) return true;
  const found = await getDocs(query(collection(db, 'staff'), where('email', '==', u.email), limit(1))).catch(() => null);
  const s = found?.docs[0];
  if (!s || s.data().uid) return false;
  const b = writeBatch(db);
  b.update(doc(db, 'staff', s.id), { uid: u.uid });
  b.set(doc(db, 'members', u.uid), { kind: 'staff', id: s.id });
  await b.commit();
  return true;
}
